import {Card, FormControl, openDialog} from 'platform/components';
import {VStack} from 'platform/foundation';

import {UseFormReturn, useWatch} from 'react-hook-form';

import {isNotNilOrEmpty} from 'ramda-adjunct';

import {Locale} from '@dms/api/base';
import {TemplateContextEnum} from '@dms/api/core';
import i18n from '@dms/i18n';

import {Nullish} from 'shared/web';

import {FormValues} from '../types/FormValues';
import {SmsLocaleTemplateGroup} from './SmsLocaleTemplateGroup';
import {SmsMessagePreview} from './SmsMessagePreview';

interface SmsChannelControlProps {
  control: FormControl<FormValues>;
  formApi: UseFormReturn<FormValues>;
  context: TemplateContextEnum | Nullish;
  locales: Locale[];
}

export function SmsChannelControl(props: SmsChannelControlProps) {
  const isMultiLocale = props.locales.length > 1;

  const isBodyError = isNotNilOrEmpty(props.formApi.formState.errors.smsBodies);

  const [isSmsEnabled, isAutomatizationEnabled] = useWatch({
    control: props.control,
    name: ['smsEnabled', 'enabled'],
  });

  const handlePreview = (locale: Locale) => {
    if (!props.context) {
      return;
    }

    const smsBody = props.formApi.getValues(`smsBodies.${locale}`) ?? '';
    openDialog(<SmsMessagePreview body={smsBody} context={props.context} />, {});
  };

  const isChannelDisabled = !isSmsEnabled || !isAutomatizationEnabled;

  return (
    <Card
      isExpandable
      isClosedByDefault
      variant="inlineWhite"
      colorStripe={isBodyError ? 'red' : undefined}
      title={i18n.t('general.labels.sms')}
      control={{
        type: 'checkbox',
        value: isSmsEnabled,
        isDisabled: !isAutomatizationEnabled,
        onChange: (value) => {
          props.formApi.setValue('smsEnabled', value);

          if (!value) {
            props.formApi.clearErrors('smsBodies');
          }
        },
      }}
    >
      <VStack spacing={6}>
        {props.locales.map((locale) => (
          <SmsLocaleTemplateGroup
            key={locale}
            locale={locale}
            control={props.control}
            formApi={props.formApi}
            context={props.context}
            isChannelDisabled={isChannelDisabled}
            isLocaleHeadingVisible={isMultiLocale}
            onPreview={handlePreview}
          />
        ))}
      </VStack>
    </Card>
  );
}
